/* Справочные данные с сервера.
 *
 * До этого каждый экран читал справочники из seed прямо в теле рендера. Худший
 * случай — фильтр по дисциплине: список значений брался из seed, а выбранное
 * значение уходило на сервер, и любое расхождение молча давало пустую выдачу.
 *
 * Справочники меняются редко и читаются почти на каждом экране, поэтому
 * держатся в кеше запросов бессрочно. Следствие: команды правки справочников
 * (Ф2.6) ОБЯЗАНЫ сбрасывать ключи qk.meta('dicts') и qk.meta('coding') —
 * иначе правка администратора не будет видна до перезагрузки вкладки.
 */
import { useMeta, useSub } from './queries'
import type { Coding, Contract, DictKey, DictRow, Dicts, User } from './types'

export interface Me {
  subject: string
  project: string
  vendor: string | null
  user: Partial<User> & { id: string; name: string }
  perms: Record<string, string>
}

export function useDicts() {
  return useMeta<Dicts>('dicts')
}

/**
 * Проектные атрибуты документа: колонки реестра заказчика, которых нет в схеме.
 *
 * <p>Набор свой у каждого проекта, поэтому читается с сервера, как справочники,
 * и той же командой сбрасывается: qk.meta('doc-attrs').
 */
export interface DocAttrDef {
  code: string
  title: string
  kind: 'text' | 'number' | 'date' | 'list'
  dict: string | null
  ord: number
}

const NO_ATTRS: DocAttrDef[] = []

export function useDocAttrs(): DocAttrDef[] {
  const { data } = useMeta<{ rows: DocAttrDef[] }>('doc-attrs')
  return data?.rows ?? NO_ATTRS
}

export function useCoding() {
  return useMeta<Coding>('coding')
}

/* Контракты — не справочник, а сущность проекта: у них есть подрядчик и сроки,
   и реестр MDR заводится на контракт, наследуя и то и другое. Читаются тем же
   способом, что справочники, потому что нужны тем же формам. Команды правки
   контрактов обязаны сбрасывать qk.meta('contracts'). */
const NO_CONTRACTS: Contract[] = []

export function useContracts(): Contract[] {
  const { data } = useMeta<Contract[]>('contracts')
  return data ?? NO_CONTRACTS
}

/* Не meta/*: это не справочник, а состояние сеанса — кто вошёл, в каком
   проекте и что ему разрешено. Один вызов вместо чтения D.ME и D.PROJECT
   на каждом экране. */
export function useMe() {
  return useSub<Me>('/me')
}

const EMPTY: DictRow[] = []

/** Строки справочника; пока не загрузился — пусто, а не значения из памяти. */
export function useDictRows(key: DictKey): DictRow[] {
  const { data } = useDicts()
  return data?.[key]?.rows ?? EMPTY
}

/** Код → наименование. Заменяет карту TDMS.DISC и её двойников по экранам. */
/**
 * Смысл кодов рассмотрения: код → «принято / принято с замечаниями /
 * переиздать». Нужен там, где код красят: цвет обязан идти от смысла, а не от
 * буквы — у следующего заказчика коды называются цифрами (V71).
 */
export function useCodeMeanings(): Record<string, string> {
  const rows = useDictRows('code')
  const out: Record<string, string> = {}
  for (const r of rows) if (r.meaning) out[r.code] = r.meaning
  return out
}

export function useDictLabels(key: DictKey): Record<string, string> {
  const rows = useDictRows(key)
  const out: Record<string, string> = {}
  for (const r of rows) out[r.code] = r.name
  return out
}
